import { Link, router } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { AdminProjectRow, Pagination } from '@/types';

interface ProjectTableProps {
  eventSlug: string;
  projects: AdminProjectRow[];
  pagination: Pagination;
}

export function ProjectTable({
  eventSlug,
  projects,
  pagination,
}: ProjectTableProps) {
  const changePage = (newPage: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', newPage.toString());
    router.get(`/admin/${eventSlug}/projects?${params.toString()}`);
  };

  if (projects.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No projects found matching your filters.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Team</TableHead>
              <TableHead>Track</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Video</TableHead>
              <TableHead>Repository</TableHead>
              <TableHead>Submission</TableHead>
              <TableHead>Last submission</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((project) => {
              const status = project.submissionStatus;
              const variant =
                status === 'submitted'
                  ? 'default'
                  : status === 'requirement_missing'
                    ? 'destructive'
                    : 'secondary';
              const label =
                status === 'submitted'
                  ? 'Submitted'
                  : status === 'requirement_missing'
                    ? 'Missing reqs'
                    : 'Unsubmitted';

              return (
                <TableRow key={project.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col gap-1">
                      <span>{project.name}</span>
                      <code className="rounded bg-muted px-2 py-1 text-xs">
                        {project.slug}
                      </code>
                    </div>
                  </TableCell>
                  <TableCell>
                    {project.team ? (
                      <Link
                        href={`/admin/${eventSlug}/teams/${project.team.slug}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {project.team.slug}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        No team
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {project.track ? (
                      <Badge variant="secondary">{project.track.name}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        No track
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{project.memberCount}</Badge>
                  </TableCell>
                  <TableCell>
                    {project.videoUrl ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <a
                          href={project.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 text-sm hover:text-blue-800"
                        >
                          View
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground text-sm">
                          No video
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {project.repoUrl ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <a
                          href={project.repoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 text-sm hover:text-blue-800"
                        >
                          View
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground text-sm">
                          No repo
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant={variant}>{label}</Badge>
                      {status === 'requirement_missing' &&
                        project.submissionError && (
                          <span
                            className="max-w-xs whitespace-pre-line text-muted-foreground text-xs"
                            title={project.submissionError}
                          >
                            {project.submissionError.length > 80
                              ? `${project.submissionError.slice(0, 80)}…`
                              : project.submissionError}
                          </span>
                        )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {project.lastSubmissionAt ? (
                      <div className="space-y-1">
                        <div className="text-sm">
                          {new Date(
                            project.lastSubmissionAt,
                          ).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {formatDistanceToNow(
                            new Date(project.lastSubmissionAt),
                            { addSuffix: true },
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        Never
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm">
                        {new Date(project.createdAt).toLocaleDateString(
                          'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          },
                        )}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {formatDistanceToNow(new Date(project.createdAt), {
                          addSuffix: true,
                        })}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" asChild>
                      <Link href={`/admin/${eventSlug}/projects/${project.id}`}>
                        View Details
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between px-4 py-4">
        <div className="text-muted-foreground text-sm">
          Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
          {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
          {pagination.total} projects
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => changePage(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter((pageNum) => {
                if (pagination.totalPages <= 7) return true;
                if (pageNum === 1 || pageNum === pagination.totalPages)
                  return true;
                if (
                  pageNum >= pagination.page - 1 &&
                  pageNum <= pagination.page + 1
                )
                  return true;
                return false;
              })
              .map((pageNum, index, array) => {
                const showEllipsis =
                  index > 0 && pageNum - array[index - 1] > 1;

                return (
                  <div key={pageNum} className="flex items-center gap-1">
                    {showEllipsis && (
                      <span className="px-2 text-muted-foreground">...</span>
                    )}
                    <Button
                      variant={
                        pageNum === pagination.page ? 'default' : 'outline'
                      }
                      size="sm"
                      onClick={() => changePage(pageNum)}
                      className="min-w-[2.5rem]"
                    >
                      {pageNum}
                    </Button>
                  </div>
                );
              })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => changePage(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
